import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OverlappingSourcingPurchaseResponse } from 'api-clients/monolith';
import _uniq from 'lodash/uniq';
import { useIntl } from 'react-intl';

import { validateSourcingPurchases } from '@/api-clients/validate-sourcing-purchases';
import { useToaster } from '@/components/base/toaster';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';
import { POOL } from '@/types/channels';

export const usePoolForm = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useToaster();
  const {
    hookForm,
    setPoolOpeningsWithConflicts,
    poolOpeningsWithConflicts,
    selectedChannelKeys,
    setSelectedOpeningIds,
  } = useJobPostingContext();
  const { setError, getValues, setValue, clearErrors } = hookForm;

  const conflictsErrorMessage = (count: number) =>
    intl.formatMessage(
      {
        defaultMessage:
          '{count, plural, one {1 opening} other {{count} openings}} have campaigns that overlap.',
        description: 'Error message for overlapping sourcing purchases',
      },
      { count },
    );

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => {
      const endAt = getValues('channels.pool.endAt');

      return validateSourcingPurchases({
        channel_slug: POOL,
        start_date: getValues('channels.pool.startsAt'),
        end_date: endAt,
        opening_ids: Object.keys(
          getValues('channels.pool.poolDetails.openingMessages'),
        ),
        is_vonq_channel: false,
        vonq_product_id: null,
      });
    },
    onSuccess: (data: OverlappingSourcingPurchaseResponse) => {
      const { conflicts } = data;
      const uniqueOpeningsWithConflicts = _uniq(
        conflicts.map(conflict => conflict.opening_id),
      );

      if (uniqueOpeningsWithConflicts.length > 0) {
        queryClient.setQueryData(queryKeys.jobPosting.poolConflicts, conflicts);
        setPoolOpeningsWithConflicts(uniqueOpeningsWithConflicts);

        notifyError({
          message: conflictsErrorMessage(uniqueOpeningsWithConflicts.length),
        });
        setError('channels.pool.endAt', {
          message: conflictsErrorMessage(uniqueOpeningsWithConflicts.length),
        });
      } else {
        queryClient.setQueryData(queryKeys.jobPosting.poolConflicts, undefined);
        setPoolOpeningsWithConflicts(undefined);

        notifySuccess({
          message: intl.formatMessage({
            defaultMessage: 'Changes saved successfully',
            description: 'Success message for saving campaign details',
          }),
        });
      }
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to save changes',
          description: 'Error message for saving campaign details',
        }),
      });
    },
  });

  /*
   * Validate the overlapping pool purchases by calling the API to validate if there are any overlapping purchases
   * Returns true if there are no conflicts, false otherwise
   */
  const validateOverlappingPoolPurchasesAsync = async (): Promise<boolean> => {
    try {
      const response = await mutateAsync();
      const hasConflicts = response?.conflicts && response.conflicts.length > 0;
      return !hasConflicts;
    } catch {
      return false;
    }
  };

  /* Remove the opening from the pool when user deletes it */
  const removeOpeningFromPool = (openingId: string) => {
    // openingMessages is Pool's source of truth for active openings.
    const openingMessages = getValues(
      'channels.pool.poolDetails.openingMessages',
    );
    const updatedMessages = { ...openingMessages };
    delete updatedMessages[openingId];
    setValue('channels.pool.poolDetails.openingMessages', updatedMessages);

    if (Object.keys(updatedMessages).length === 0) {
      // No openings left — remove the pool channel entirely.
      const channels = getValues('channels');
      const { pool: _drop, ...rest } = channels;
      setValue('channels', rest);
    } else {
      // Keep openingBudgets in sync — use-opening.ts reads it to determine
      // which channels an opening belongs to.
      const openingBudgets = getValues('channels.pool.openingBudgets');
      const updatedBudgets = { ...openingBudgets };
      delete updatedBudgets[openingId];
      setValue('channels.pool.openingBudgets', updatedBudgets);
    }

    // If Pool was the only selected channel, also remove the opening from the
    // global selected openings list.
    if (selectedChannelKeys.length === 1)
      setSelectedOpeningIds(prev => prev.filter(id => id !== openingId));
  };

  /* Remove the conflicting opening from the channel when user deletes it */
  const removeConflictingOpening = (openingId: string) => {
    const updatedConflicts = poolOpeningsWithConflicts?.filter(
      id => id !== openingId,
    );
    setPoolOpeningsWithConflicts(updatedConflicts);

    removeOpeningFromPool(openingId);

    if (updatedConflicts && updatedConflicts.length > 0) {
      setError('channels.pool.endAt', {
        message: conflictsErrorMessage(updatedConflicts?.length ?? 0),
      });
    } else {
      clearErrors('channels.pool.endAt');
    }
  };

  return {
    validateOverlappingPoolPurchasesAsync,
    isValidatingPoolPurchases: isPending,
    conflicts: queryClient.getQueryData<
      OverlappingSourcingPurchaseResponse['conflicts']
    >(queryKeys.jobPosting.poolConflicts),
    removeConflictingOpening,
    removeOpeningFromPool,
  };
};
